import { useLocation, Link } from "react-router-dom";
import AppSidebar from "./AppSidebar";
import { ChevronRight, Home } from "lucide-react";

const routeLabels = {
  "/builder": "Builder Overview",
  "/builder/setup": "Project Operating Model",
  "/builder/new-mail": "New Approvals Mail",
  "/builder/rfi-war-room": "Council Requests",
  "/builder/decision-import": "Decision Import",
  "/builder/stage-conditions": "Stage Conditions",
  "/builder/proof-review": "Proof Review",
  "/builder/stage-readiness": "Stage Readiness",
  "/builder/inspection-readiness": "Inspection Readiness",
  "/builder/job-blocker": "Job Blocker",
  "/builder/job-blocker-copilot": "Job Blocker Copilot",
  "/builder/trade-release": "Trade Release",
  "/builder/release-calendar": "Release Calendar",
  "/builder/site-ready-pack": "Site Ready Pack",
  "/builder/pre-submission-check": "External Handover Control",
  "/builder/scope-boundary-ledger": "Scope Boundary Ledger",
  "/builder/readiness-dossier": "Readiness Dossier",
  "/builder/whatsapp-action-rail": "WhatsApp Action Rail",
  "/builder/email-action-rail": "Email Action Rail",
  "/surveyor": "Surveyor Overview",
  "/surveyor/incoming-packs": "Incoming Packs",
  "/surveyor/review-centre": "Review Centre",
  "/surveyor/closeout-view": "Closeout View",
  "/shared/client-portal": "Client Portal",
  "/shared/buyer-handover": "Buyer Handover",
};

const routeDescriptions = {
  "/builder": "What is blocking progress, who owns it, and what needs action today.",
  "/builder/setup": "Choose how permits, consultants, proof, and stage readiness work in this project.",
  "/builder/new-mail": "Emails from Outlook or Gmail land here automatically. AI classifies them into council requests, decisions, proof, or general.",
  "/builder/rfi-war-room": "Turn a council request into clear actions, assign who owns each response, and draft follow-ups.",
  "/builder/decision-import": "Upload the approval letter and turn conditions into staged actions. Review before publishing.",
  "/builder/stage-conditions": "Approval conditions affecting the current milestone and what is still missing to clear them.",
  "/builder/proof-review": "Files and photos arrive here first. Once reviewed, they update the linked condition and may clear the stage.",
  "/builder/stage-readiness": "Whether the next stage is truly ready. Not just scheduled.",
  "/builder/inspection-readiness": "Is the next inspection stage ready before you notify the surveyor or release the next trade.",
  "/builder/job-blocker": "See what is stopping the job, who needs to act, and what clears next.",
  "/builder/job-blocker-copilot": "Ask anything about your blockers, readiness, next steps, or draft a message.",
  "/builder/trade-release": "Which trades are safe to release, which are on hold, and what is blocking them.",
  "/builder/release-calendar": "See readiness-driven follow-ups, trade release timing, and stage-related actions in one operational calendar.",
  "/builder/site-ready-pack": "Give site teams a simple pack showing what must be done, what proof is needed, and how to upload.",
  "/builder/pre-submission-check": "Review the handover from consultants or lodgement pack quality before the next workflow.",
  "/builder/scope-boundary-ledger": "See what is in builder scope, what is owner-supplied, what is owner-appointed, and how it affects readiness.",
  "/builder/readiness-dossier": "Generate a premium stage readiness pack for internal review or surveyor submission.",
  "/builder/whatsapp-action-rail": "Blocker-linked WhatsApp threads. Fast chase. Frictionless external response. No login required.",
  "/builder/email-action-rail": "Formal requests, consultant communication, certificate chasers, and surveyor-ready emails.",
  "/surveyor": "Incoming packs, proof waiting review, and what still needs clarification.",
  "/surveyor/incoming-packs": "Packs sent by builders for your review.",
  "/surveyor/review-centre": "Review proof against the linked condition, request more if needed.",
  "/surveyor/closeout-view": "Final pack summary, accepted proof, and remaining clarifications.",
  "/shared/client-portal": "Curated project updates for clients. Internal workflow stays private.",
  "/shared/buyer-handover": "Clean handover pack for spec homes or completed homes sold to buyers.",
};

export default function AppLayout({ children }) {
  const location = useLocation();
  const path = location.pathname;
  const label = routeLabels[path] || "Page";
  const description = routeDescriptions[path] || "";

  const segments = path.split("/").filter(Boolean);
  const breadcrumbs = segments.map((seg, i) => {
    const href = "/" + segments.slice(0, i + 1).join("/");
    const segLabel = routeLabels[href] || seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return { label: segLabel, href, isLast: i === segments.length - 1 };
  });

  return (
    <div className="min-h-screen bg-slate-50/50">
      <AppSidebar />
      <div className="ml-[240px] min-h-screen">
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/60" data-testid="app-header">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3">
            <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-1" data-testid="breadcrumbs">
              <Link to="/" className="hover:text-slate-600 transition-colors"><Home className="w-3.5 h-3.5" /></Link>
              {breadcrumbs.map((crumb) => (
                <span key={crumb.href} className="flex items-center gap-1.5">
                  <ChevronRight className="w-3 h-3" />
                  {crumb.isLast ? (
                    <span className="text-slate-600 font-medium">{crumb.label}</span>
                  ) : (
                    <Link to={crumb.href} className="hover:text-slate-600 transition-colors">{crumb.label}</Link>
                  )}
                </span>
              ))}
            </nav>
            <h1 className="font-heading font-semibold text-xl text-slate-900" data-testid="page-title">{label}</h1>
            {description && <p className="text-sm text-slate-500 mt-0.5 max-w-2xl" data-testid="page-description">{description}</p>}
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-6 lg:px-8 py-6 page-enter" data-testid="page-content">{children}</main>
      </div>
    </div>
  );
}
